import { now } from '../../utils/clock';
/**
 * diagnostics/diagnosticsStore.ts
 *
 * Optional runtime diagnostics mode for MER Algo Footprints v4.2+
 *
 * When enabled, captures a structured event log of all internal decisions:
 *   - change detection events
 *   - AI run decisions (skipped / executed)
 *   - thesis changes
 *   - ranking recomputes
 *   - alert emissions
 *   - provider reconnect attempts
 *   - signal confidence scores
 *
 * Non-intrusive: disabled by default, zero overhead when off.
 * Enable via Settings → Diagnostics toggle or keyboard shortcut.
 */

export type DiagnosticEventType =
  | 'change_detected'
  | 'ai_run_executed'
  | 'ai_run_skipped'
  | 'thesis_changed'
  | 'thesis_unchanged'
  | 'ranking_recomputed'
  | 'alert_emitted'
  | 'provider_reconnect'
  | 'signal_scored'
  | 'context_built'
  | 'htf_derived'
  | 'bridge_received'
  | 'cooldown_active'
  | 'data_quality_warn'
  | 'regime_shift'
  | 'readiness_upgraded';

export type DiagnosticSeverity = 'info' | 'warn' | 'success' | 'error';

export interface DiagnosticEvent {
  id:        number;
  ts:        number;
  type:      DiagnosticEventType;
  severity:  DiagnosticSeverity;
  symbol?:   string;
  message:   string;
  detail?:   string;           // optional extra detail (collapsed by default)
  value?:    number;           // optional numeric value (score, count, etc.)
}

// ─── Store ────────────────────────────────────────────────────────────────────

const MAX_EVENTS = 200;
const DIAG_STORAGE_KEY = 'mer_diagnostics_enabled';
let _enabled = (() => {
  try { return localStorage.getItem(DIAG_STORAGE_KEY) === 'true'; } catch { return false; }
})();
let _seq       = 0;
const _events: DiagnosticEvent[] = [];
const _listeners = new Set<() => void>();

// ─── Public API ───────────────────────────────────────────────────────────────

export function isDiagnosticsEnabled(): boolean { return _enabled; }

export function setDiagnosticsEnabled(on: boolean): void {
  _enabled = on;
  try { localStorage.setItem(DIAG_STORAGE_KEY, String(on)); } catch { /* ignore */ }
  if (on) logEvent('info', 'context_built', 'Diagnostics mode enabled', undefined, undefined);
  _notify();
}

export function logDiagnostic(
  severity: DiagnosticSeverity,
  type:     DiagnosticEventType,
  message:  string,
  symbol?:  string,
  detail?:  string,
  value?:   number
): void {
  if (!_enabled) return;
  const event: DiagnosticEvent = {
    id: ++_seq, ts: now(), type, severity, symbol, message, detail, value,
  };
  _events.unshift(event);
  if (_events.length > MAX_EVENTS) _events.splice(MAX_EVENTS);
  _notify();
}

// Convenience wrappers
export function logEvent(severity: DiagnosticSeverity, type: DiagnosticEventType, msg: string, sym?: string, detail?: string, value?: number) {
  logDiagnostic(severity, type, msg, sym, detail, value);
}
export function logAIRun(symbol: string, ran: boolean, reason: string, tokens?: number) {
  logDiagnostic(ran ? 'success' : 'info', ran ? 'ai_run_executed' : 'ai_run_skipped',
    ran ? `AI run executed for ${symbol}` : `AI skipped: ${reason}`, symbol, reason, tokens);
}
export function logThesisChange(symbol: string, changed: boolean, from?: string, to?: string) {
  logDiagnostic(changed ? 'success' : 'info', changed ? 'thesis_changed' : 'thesis_unchanged',
    changed ? `Thesis changed: ${from} → ${to}` : 'Thesis unchanged after AI run', symbol,
    changed ? `${from} → ${to}` : undefined);
}
export function logAlertEmitted(symbol: string, alertType: string, severity: string) {
  logDiagnostic('warn', 'alert_emitted', `Alert: ${alertType} (${severity})`, symbol, alertType);
}
export function logCooldown(symbol: string, remainingSec: number) {
  logDiagnostic('info', 'cooldown_active', `AI cooldown: ${remainingSec}s remaining`, symbol, undefined, remainingSec);
}
export function logRegimeShift(symbol: string, from: string, to: string, confidence: number) {
  logDiagnostic('warn', 'regime_shift', `Regime: ${from} → ${to} (${confidence}%)`, symbol, undefined, confidence);
}
export function logReadinessUpgrade(symbol: string, setup: string, from: string, to: string) {
  logDiagnostic('success', 'readiness_upgraded', `Readiness: ${setup} → ${to}`, symbol, `${from} → ${to}`);
}
export function logDataQualityWarn(symbol: string, warnings: string[]) {
  if (!warnings.length) return;
  logDiagnostic('warn', 'data_quality_warn', `Data quality: ${warnings[0]}`, symbol,
    warnings.join(' · '));
}
export function logHTFDerived(symbol: string, pair: string, bars: number, confidence: string) {
  logDiagnostic('info', 'htf_derived', `HTF derived: ${pair} (${bars} bars, ${confidence})`, symbol);
}

export function getDiagnosticEvents(): DiagnosticEvent[] { return [..._events]; }

export function clearDiagnosticEvents(): void {
  _events.splice(0);
  _notify();
}

export function subscribeDiagnostics(fn: () => void): () => void {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function _notify() { _listeners.forEach(fn => fn()); }

// ─── Severity color map ───────────────────────────────────────────────────────

export const DIAG_COLORS: Record<DiagnosticSeverity, string> = {
  info:    'var(--blue-muted)',
  warn:    'var(--amber)',
  success: 'var(--green)',
  error:   'var(--red)',
};

export const DIAG_ICONS: Record<DiagnosticEventType, string> = {
  change_detected:    '◎',
  ai_run_executed:    '⚡',
  ai_run_skipped:     '○',
  thesis_changed:     '✦',
  thesis_unchanged:   '·',
  ranking_recomputed: '↕',
  alert_emitted:      '▲',
  provider_reconnect: '⟳',
  signal_scored:      '◈',
  context_built:      '◇',
  htf_derived:        '⊞',
  bridge_received:    '⇒',
  cooldown_active:    '⏸',
  data_quality_warn:  '⚠',
  regime_shift:       '⇄',
  readiness_upgraded: '●',
};
